import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import PreLaunchBanner from '../../components/common/PreLaunchBanner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, authError, setAuthError } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error('Please fill in all fields.');

    setLoading(true);
    setAuthError(null);

    try {
      const result = await login(email, password);

      if (result.requiresPin) {
        navigate('/admin-pin');
      } else if (result.requiresOTP) {
        navigate('/verify-otp');
      } else {
        toast.success('Welcome back!');
        // Route straight to the role's area. ProtectedRoute then handles a pending/
        // changes-requested driver by sending them to their application-status page.
        const role = result.role;
        if (role === 'driver') navigate('/driver');
        else if (role === 'rider') navigate('/rider');
        else if (role === 'admin' || role === 'superAdmin') navigate('/admin');
        else navigate('/');
      }
    } catch (err) {
      // Error already set in context
    } finally {
      setLoading(false);
    }
  };

  const EyeIcon = ({ show, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer p-1 flex items-center justify-center rounded-md transition-colors hover:bg-black/5"
      tabIndex={-1}
      aria-label={show ? 'Hide password' : 'Show password'}
    >
      {show ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )}
    </button>
  );

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      {/* LEFT PANEL — form */}
      <div className="flex-1 bg-brand-cream flex items-center justify-center px-5 py-8 sm:px-6 md:px-8 md:py-12 lg:px-12">
        <div className="w-full max-w-md">
          <PreLaunchBanner variant="full" />

          <Link to="/" className="flex items-center gap-3 mt-6 mb-8 md:mb-12">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center font-extrabold text-lg text-brand-dark bg-gradient-to-br from-brand-primary to-brand-primary-light shadow-brand">
              R
            </div>
            <span className="font-serif text-2xl text-brand-dark">Reservo</span>
          </Link>

          <h1 className="font-serif text-3xl md:text-4xl text-brand-dark mb-2 tracking-tight">
            Welcome back
          </h1>
          <p className="text-body text-base leading-relaxed mb-6">
            Sign in to manage your rides, track trips, and more.
          </p>

          <form onSubmit={handleSubmit} className="mb-6">
            <div className="form-group">
              <label className="form-label">Email address</label>
              <input
                type="email"
                className="form-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label flex justify-between items-center">
                <span>Password</span>
                <Link to="/forgot-password" className="text-brand-primary font-semibold text-sm no-underline">
                  Forgot password?
                </Link>
              </label>
              <div className="relative flex items-center">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input pr-11"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <EyeIcon show={showPassword} onClick={() => setShowPassword(!showPassword)} />
              </div>
            </div>

            {authError && (
              <div className="form-error mb-4">
                <span className="mr-1.5">⚠</span>{authError}
              </div>
            )}

            <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={loading}>
              {loading ? <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> : 'Sign In'}
            </button>
          </form>

          <p className="text-body text-sm text-center mb-2">
            Don't have an account?{' '}
            <Link to="/register" className="text-brand-primary font-semibold">Register as a rider</Link>
          </p>
          <p className="text-muted text-xs text-center">
            Want to drive?{' '}
            <Link to="/driver/register" className="text-brand-primary font-semibold">Apply as a driver</Link>
          </p>
        </div>
      </div>

      {/* RIGHT PANEL — marketing cards */}
      <div className="flex-1 bg-brand-dark flex items-center justify-center px-5 py-8 md:px-8 md:py-12 lg:px-12">
        <div className="w-full max-w-md flex flex-col gap-6">
          <div className="bg-white/[0.04] border border-white/[0.08] rounded-card p-6 md:p-7">
            <div className="font-bold text-lg text-brand-primary mb-1">Pre-booked</div>
            <div className="text-white/50 text-sm">Rides with upfront pricing</div>
          </div>
          <div className="bg-white/[0.04] border border-white/[0.08] rounded-card p-6 md:p-7">
            <div className="font-bold text-lg text-brand-primary mb-1">NSW Licensed</div>
            <div className="text-white/50 text-sm">Point-to-Point compliant</div>
          </div>
          <div className="bg-white/[0.04] border border-white/[0.08] rounded-card p-6 md:p-7">
            <div className="font-bold text-lg text-brand-primary mb-1">Your Driver</div>
            <div className="text-white/50 text-sm">Choose preferred drivers</div>
          </div>
        </div>
      </div>
    </div>
  );
}